/**
 * ============================================================
 * LOCATION SELECTION MODAL - City/State/Country Selection
 * ============================================================
 * 
 * A modal popup for selecting user's location with cascading dropdowns.
 * Appears after login if no location is saved.
 * 
 * Features:
 * - Country dropdown (cached, fetched once)
 * - State dropdown (fetched on country selection)
 * - City dropdown (fetched on state selection)
 * ============================================================
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Select, Button, Typography, Spin, Flex, Alert } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import '../../assets/styles/components_styles/location.css';
import {
    fetchCountries,
    fetchStatesByCountry,
    fetchCitiesByState,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    confirmLocationSelection,
    hideLocationModal,
    selectCountries,
    selectCountriesLoading,
    selectStates,
    selectStatesLoading,
    selectCities,
    selectCitiesLoading,
    selectSelectedCountry,
    selectSelectedState,
    selectSelectedCity,
    selectShowLocationModal,
    clearSavedLocation,
} from '../../redux/locationSlice';

const { Title, Text } = Typography;

/**
 * Dropdown Loading Content
 * Shows inside select dropdown when loading
 */
const DropdownLoader = ({ text = 'Loading...' }) => (
    <div className="dropdownLoader">
        <Spin size="small" indicator={<LoadingOutlined style={{ fontSize: 16, color: '#2563eb' }} spin />} />
        <span style={{ marginLeft: 8, color: '#717171' }}>{text}</span>
    </div>
);

export default function LocationSelectionModal() {
    const [currentCity, setCurrentCity] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const dispatch = useDispatch();

    // ========== SELECTORS ==========
    const showModal = useSelector(selectShowLocationModal);
    const countriesRaw = useSelector(selectCountries);
    const countriesLoading = useSelector(selectCountriesLoading);
    const statesRaw = useSelector(selectStates);
    const statesLoading = useSelector(selectStatesLoading);
    const citiesRaw = useSelector(selectCities);
    const citiesLoading = useSelector(selectCitiesLoading);
    const selectedCountry = useSelector(selectSelectedCountry);
    const selectedState = useSelector(selectSelectedState);
    const selectedCity = useSelector(selectSelectedCity);

    // Ensure arrays are always safe to map
    const countries = Array.isArray(countriesRaw) ? countriesRaw : [];
    const states = Array.isArray(statesRaw) ? statesRaw : [];
    const cities = Array.isArray(citiesRaw) ? citiesRaw : [];

    // Check if any data is loading
    const isAnyLoading = countriesLoading || statesLoading || citiesLoading;

    // ========== EFFECTS ==========

    // Set current city at initial load
    useEffect(() => {
        if (showModal && selectedCity && selectedCity?.id) {
            setCurrentCity(selectedCity);
        }
    }, [showModal]);

    // Fetch countries when modal opens
    useEffect(() => {
        if (showModal) {
            dispatch(fetchCountries());
        }
    }, [showModal, dispatch]);

    // Fetch states if modal opens with a previously selected country
    useEffect(() => {
        if (showModal && selectedCountry?.code && states.length === 0) {
            dispatch(fetchStatesByCountry(selectedCountry.code));
        }
    }, [showModal, selectedCountry, states.length, dispatch]);

    // Fetch cities if modal opens with a previously selected state
    useEffect(() => {
        if (showModal && selectedState?.id && cities.length === 0) {
            dispatch(fetchCitiesByState(selectedState.id));
        }
    }, [showModal, selectedState, cities.length, dispatch]);

    // ========== HANDLERS ==========

    /**
     * Handle country selection
     * Fetches states for the selected country
     */
    const handleCountryChange = (value, option) => {
        const country = {
            id: value,
            name: option.label,
            code: option.code,
        };
        dispatch(setSelectedCountry(country));
        dispatch(fetchStatesByCountry(option.code));
    };

    /**
     * Handle state selection
     * Fetches cities for the selected state
     */
    const handleStateChange = (value, option) => {
        const state = {
            id: value,
            name: option.label,
        };
        dispatch(setSelectedState(state));
        dispatch(fetchCitiesByState(value));
    };

    /**
     * Handle city selection
     */
    const handleCityChange = (value, option) => {
        const city = {
            id: value,
            name: option.label,
        };
        dispatch(setSelectedCity(city));
        if (!currentCity) {
            setCurrentCity(city);
            setShowAlert(true);
        }
    };

    /**
     * Confirm selection and close modal
     */
    const handleConfirm = () => {
        dispatch(confirmLocationSelection());
        setCurrentCity(null);
        setShowAlert(false);
    };

    /**
     * Skip location selection (close modal without saving)
     */
    const handleSkip = () => {
        if (currentCity) {
            dispatch(setSelectedCity(currentCity));
        } else {
            dispatch(clearSavedLocation());
        }
        dispatch(hideLocationModal());
        setCurrentCity(null);
        setShowAlert(false);
    };

    // ========== RENDER ==========
    return (
        <Modal
            open={showModal}
            onCancel={handleSkip}
            footer={null}
            centered
            width={480}
            className="locationModal"
            maskClosable={false}
        >
            <div className="locationModalContent">
                {/* Header */}
                <div className="locationModalHeader">
                    <div className="locationIconWrapper">
                        <span className="material-symbols-outlined locationIcon">
                            location_on
                        </span>
                    </div>
                    <Title level={3} className="locationModalTitle">
                        Select Your Location
                    </Title>
                    <Text className="locationModalSubtitle">
                        Choose your location to see relevant events near you
                    </Text>
                </div>

                {/* Selection Form */}
                <div className="locationSelectionForm">
                    {/* Country Dropdown */}
                    <div className="locationFormItem">
                        <label className="locationLabel">
                            Country
                            {countriesLoading && <Spin size="small" className="labelLoader" />}
                        </label>
                        <Select
                            showSearch
                            placeholder="Select Country"
                            size="large"
                            className="locationSelect"
                            loading={countriesLoading}
                            value={selectedCountry?.id}
                            onChange={handleCountryChange}
                            optionFilterProp="label"
                            notFoundContent={
                                countriesLoading ? (
                                    <DropdownLoader text="Loading countries..." />
                                ) : (
                                    'No countries found'
                                )
                            }
                            options={countries.map((country) => ({
                                value: country.id,
                                label: country.name,
                                code: country.code,
                            }))}
                        />
                    </div>

                    {/* State Dropdown */}
                    <div className="locationFormItem">
                        <label className="locationLabel">
                            State
                            {statesLoading && <Spin size="small" className="labelLoader" />}
                        </label>
                        <Select
                            showSearch
                            placeholder="Select State"
                            size="large"
                            className="locationSelect"
                            loading={statesLoading}
                            value={selectedState?.id}
                            onChange={handleStateChange}
                            disabled={!selectedCountry || statesLoading}
                            optionFilterProp="label"
                            notFoundContent={
                                statesLoading ? (
                                    <DropdownLoader text="Loading states..." />
                                ) : !selectedCountry ? (
                                    'Select a country first'
                                ) : (
                                    'No states found'
                                )
                            }
                            options={states.map((state) => ({
                                value: state.id,
                                label: state.name,
                            }))}
                        />
                    </div>

                    {/* City Dropdown */}
                    <div className="locationFormItem">
                        <label className="locationLabel">
                            City
                            {citiesLoading && <Spin size="small" className="labelLoader" />}
                        </label>
                        <Select
                            showSearch
                            placeholder="Select City"
                            size="large"
                            className="locationSelect"
                            loading={citiesLoading}
                            value={selectedCity?.id}
                            onChange={handleCityChange}
                            disabled={!selectedState || citiesLoading}
                            optionFilterProp="label"
                            notFoundContent={
                                citiesLoading ? (
                                    <DropdownLoader text="Loading cities..." />
                                ) : !selectedState ? (
                                    'Select a state first'
                                ) : (
                                    'No cities found'
                                )
                            }
                            options={cities.map((city) => ({
                                value: city.id,
                                label: city.name,
                            }))}
                        />
                    </div>
                </div>

                {/* Info Alert */}
                {(showAlert || (selectedCity && selectedCity.id !== currentCity?.id)) && (
                    <Alert
                        message={`Location set to ${selectedCity.name}`}
                        type="success"
                        showIcon
                        className="locationAlert"
                    />
                )}

                {/* Action Buttons */}
                <Flex gap={12} className="locationActions">
                    <Button
                        size="large"
                        onClick={handleSkip}
                        className="locationSkipBtn"
                    >
                        Skip for now
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleConfirm}
                        disabled={!selectedCity || isAnyLoading}
                        loading={isAnyLoading}
                        className="locationConfirmBtn btnGradient"
                    >
                        {isAnyLoading ? 'Loading...' : 'Confirm Location'}
                    </Button>
                </Flex>
            </div>
        </Modal>
    );
}
